/**
 * Advanced Settings (byok-architecture §E.1).
 *
 * Path: /settings/advanced. Power-user surface, reached via the sidebar
 * "Settings" link. Two cards at MVP:
 *
 *   1. AI Provider (BYOK) — connect / test / disconnect an Anthropic key.
 *      When connected, user-class AI calls (capture, future chat) route
 *      through the tenant's own key and the "Your AI" meter goes inert.
 *   2. AI Capacity — read-only view of the two-meter usage state.
 *
 * The MCP Access card (§E.3) ships with D.4 — it depends on the MCP tool
 * surface, which is Phase D.
 *
 * OpenAI BYOK is accepted by the storage layer but not surfaced here —
 * only Anthropic is a routable BYOK provider at MVP (implementation-plan
 * §3.2 defers OpenAI BYOK).
 */
import { useEffect, useState } from 'preact/hooks';
import {
  type ByokSettings,
  type CapThreshold,
  type MeResponse,
  connectByok,
  disconnectByok,
  getBillingPortalLink,
  getByokSettings,
  testByok,
} from '../api.ts';
import { Icons } from '../components/icons';

interface SettingsProps {
  me: MeResponse;
}

export function Settings({ me }: SettingsProps) {
  const [byok, setByok] = useState<ByokSettings | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getByokSettings()
      .then((s) => {
        if (!cancelled) setByok(s);
      })
      .catch((err: Error) => {
        if (!cancelled) setLoadErr(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div class="v2-page">
      <div class="page-head">
        <div>
          <div class="page-eyebrow">Settings</div>
          <h1 class="v2-page-title">Advanced</h1>
          <div class="page-sub">
            Bring your own AI provider and review your monthly AI capacity.
          </div>
        </div>
      </div>

      <AiProviderCard byok={byok} loadErr={loadErr} onChange={setByok} />
      <AiCapacityCard me={me} />
      <BillingCard me={me} />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Billing card — onboarding §3.7 (Manage subscription → Stripe Customer Portal)
// ───────────────────────────────────────────────────────────────────────────

/**
 * "Manage subscription" → Stripe Customer Portal redirect. The portal is
 * Stripe-hosted: update card, cancel, view invoices. Cancel there fires the
 * `customer.subscription.updated` webhook, which the worker maps to
 * `cancel_pending` (lifecycle §3.4) — no PWA-side handling needed.
 *
 * Founder / comp tenants have no Stripe relationship — the card shows a
 * plain "included" note instead of the portal button.
 *
 * Placement note: onboarding §3.7 says "Settings → Billing"; at MVP the only
 * settings surface is this Advanced page, so the Billing card lives here.
 */
function BillingCard({ me }: { me: MeResponse }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isComp = me.tenant.subscription_status === 'comp';

  async function openPortal() {
    setBusy(true);
    setError(null);
    try {
      const { url } = await getBillingPortalLink();
      window.location.href = url;
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <div class="panel" style={{ marginTop: 16 }}>
      <div class="panel-head">
        <h2 class="panel-title">Billing</h2>
      </div>
      {isComp ? (
        <p class="muted" style={{ fontSize: 13 }}>
          Your workspace is on a complimentary plan — no billing to manage.
        </p>
      ) : (
        <>
          <p class="muted" style={{ marginBottom: 12, fontSize: 13 }}>
            Update your card, view invoices, or cancel — all through Stripe's secure portal.
          </p>
          {error && (
            <p class="error-banner" style={{ marginBottom: 12 }}>
              {error}
            </p>
          )}
          <button type="button" class="btn" disabled={busy} onClick={openPortal}>
            {busy ? 'Opening…' : 'Manage subscription'}
          </button>
        </>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// AI Provider (BYOK) card — byok-architecture §E.2
// ───────────────────────────────────────────────────────────────────────────

function AiProviderCard({
  byok,
  loadErr,
  onChange,
}: {
  byok: ByokSettings | null;
  loadErr: string | null;
  onChange: (s: ByokSettings) => void;
}) {
  const [connecting, setConnecting] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [formErr, setFormErr] = useState<string | null>(null);
  const [busyProvider, setBusyProvider] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ provider: string; ok: boolean } | null>(null);

  const anthropic = byok?.providers.find((p) => p.provider === 'anthropic') ?? null;

  async function handleConnect(e: Event) {
    e.preventDefault();
    if (!apiKey.trim()) return;
    setConnecting(true);
    setFormErr(null);
    try {
      await connectByok('anthropic', apiKey.trim());
      const fresh = await getByokSettings();
      onChange(fresh);
      setApiKey('');
      setFormOpen(false);
    } catch (err) {
      setFormErr((err as Error).message);
    } finally {
      setConnecting(false);
    }
  }

  async function handleDisconnect(provider: 'anthropic') {
    setBusyProvider(provider);
    setTestResult(null);
    try {
      const fresh = await disconnectByok(provider);
      onChange(fresh);
    } catch (err) {
      setFormErr((err as Error).message);
    } finally {
      setBusyProvider(null);
    }
  }

  async function handleTest(provider: 'anthropic') {
    setBusyProvider(provider);
    setTestResult(null);
    try {
      const r = await testByok(provider);
      setTestResult({ provider, ok: r.valid });
    } catch (err) {
      setFormErr((err as Error).message);
    } finally {
      setBusyProvider(null);
    }
  }

  return (
    <div class="panel" style={{ marginBottom: 16 }}>
      <div class="panel-head">
        <h2 class="panel-title">AI Provider</h2>
      </div>

      {loadErr && <p class="error-banner">{loadErr}</p>}

      {!byok && !loadErr && <p class="muted">Loading…</p>}

      {byok && anthropic && (
        <div>
          <p style={{ marginBottom: 8 }}>
            <Icons.Check size={14} className="ico" /> <strong>Connected: Anthropic</strong>{' '}
            <code style={{ color: 'var(--text-muted)' }}>{anthropic.key_hint}</code>
          </p>
          <p class="muted" style={{ marginBottom: 12, fontSize: 13 }}>
            Added {new Date(anthropic.added_at).toLocaleDateString()}. Your capture and chat AI
            calls route through this key — the "Your AI" meter below is inert while it's connected.
            System features (briefing, classification) always use the bundled subscription.
          </p>
          {testResult?.provider === 'anthropic' && (
            <p
              class={testResult.ok ? 'muted' : 'error-banner'}
              style={{ marginBottom: 12, fontSize: 13 }}
            >
              {testResult.ok
                ? 'Connection test passed — your key is working.'
                : 'Connection test failed — your key may be invalid or out of credit.'}
            </p>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              class="btn"
              disabled={busyProvider === 'anthropic'}
              onClick={() => handleTest('anthropic')}
            >
              {busyProvider === 'anthropic' ? 'Testing…' : 'Test Connection'}
            </button>
            <button
              type="button"
              class="btn"
              disabled={busyProvider === 'anthropic'}
              onClick={() => handleDisconnect('anthropic')}
            >
              Disconnect
            </button>
          </div>
        </div>
      )}

      {byok && !anthropic && !formOpen && (
        <div>
          <p class="muted" style={{ marginBottom: 12 }}>
            AI is included with your subscription. To route your capture and chat calls through your
            own API key instead, connect a provider.
          </p>
          <button type="button" class="btn btn-primary" onClick={() => setFormOpen(true)}>
            Connect Anthropic
          </button>
          <p class="muted" style={{ marginTop: 8, fontSize: 12 }}>
            OpenAI BYOK is coming in a later release.
          </p>
        </div>
      )}

      {byok && !anthropic && formOpen && (
        <form onSubmit={handleConnect}>
          <label
            for="byok-key"
            style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600 }}
          >
            Anthropic API key
          </label>
          <input
            id="byok-key"
            type="password"
            class="search-bar"
            placeholder="sk-ant-…"
            value={apiKey}
            onInput={(e) => setApiKey((e.target as HTMLInputElement).value)}
            style={{ marginBottom: 8, width: '100%' }}
          />
          <p class="muted" style={{ marginBottom: 12, fontSize: 12 }}>
            Your key is encrypted at rest and used only to route your AI calls. We never log keys in
            plain text. You can disconnect at any time; AI calls resume through the bundled
            subscription.
          </p>
          {formErr && (
            <p class="error-banner" style={{ marginBottom: 12 }}>
              {formErr}
            </p>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="submit"
              class="btn btn-primary"
              disabled={connecting || apiKey.trim().length < 10}
            >
              {connecting ? 'Validating…' : 'Connect'}
            </button>
            <button
              type="button"
              class="btn"
              disabled={connecting}
              onClick={() => {
                setFormOpen(false);
                setApiKey('');
                setFormErr(null);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {formErr && byok && anthropic && (
        <p class="error-banner" style={{ marginTop: 12 }}>
          {formErr}
        </p>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// AI Capacity card — byok-architecture §E.5 (read-only)
// ───────────────────────────────────────────────────────────────────────────

function AiCapacityCard({ me }: { me: MeResponse }) {
  const { user, system } = me.ai_usage;
  const byokOn = me.tenant.byok_enabled;

  return (
    <div class="panel">
      <div class="panel-head">
        <h2 class="panel-title">AI Capacity</h2>
      </div>
      <p class="muted" style={{ marginBottom: 16, fontSize: 13 }}>
        Two monthly meters. "Your AI" covers capture and chat; "System AI" covers briefing and
        classification. Both reset on the 1st.
      </p>

      {byokOn ? (
        <Meter label="Your AI" pct={0} threshold="normal" hint="Routed via your own key" />
      ) : (
        <Meter label="Your AI" pct={user.pct} threshold={user.threshold} />
      )}
      <Meter label="System AI" pct={system.pct} threshold={system.threshold} />
    </div>
  );
}

function Meter({
  label,
  pct,
  threshold,
  hint,
}: {
  label: string;
  pct: number;
  threshold: CapThreshold;
  hint?: string;
}) {
  const color =
    threshold === 'over_150' || threshold === 'at_100'
      ? 'var(--danger)'
      : threshold === 'warn_80'
        ? 'var(--warn)'
        : 'var(--accent)';
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 13,
          marginBottom: 4,
        }}
      >
        <span style={{ fontWeight: 600 }}>{label}</span>
        <span class="muted">{hint ?? `${pct}% used`}</span>
      </div>
      <div
        style={{
          height: 6,
          borderRadius: 3,
          background: 'var(--border)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${Math.min(100, pct)}%`,
            background: color,
            transition: 'width 200ms',
          }}
        />
      </div>
    </div>
  );
}
